import { useEffect } from 'react';

import { inputCommands } from 'app/modules/wallforming/components/input-binding/commands/inputCommands';
import { getClickCount } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { inputActions } from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputFocus } from 'app/modules/wallforming/components/input-binding/types/inputFocus';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import {
    DOUBLE_CLICK_MS,
    DRAG_THRESHOLD_PX,
    chordFromKeyboardEvent,
    chordFromMouseEvent,
    formatChord
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import {
    isContextActive,
    resolveInputContext
} from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';
import { updateInputPointerPosition } from 'app/modules/wallforming/components/scenes/objects/utils/pointerUtils';
import { isTextInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { isPanelQuickReplacementInwardModifierActive } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { MAIN_DIV_ID } from 'shared/constants';

import type { InputChord } from 'app/modules/wallforming/components/input-binding/types/inputChord';
import type { InputEvent } from 'app/modules/wallforming/components/input-binding/types/inputEvent';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

function isFocusAllowed(focus: InputFocus, event: InputEvent, isInputBindingPanelOpen: boolean): boolean {
    if (isInputBindingPanelOpen) {
        return true;
    }

    switch (focus) {
        case InputFocus.Any:
            return true;
        case InputFocus.NoTextInput:
            return !isTextInputElement(event.target);
        case InputFocus.Scene:
            return (
                !(event instanceof KeyboardEvent) ||
                (event.target instanceof Element &&
                    (event.target.id === MAIN_DIV_ID || event.target.closest('[data-input-scene]') !== null))
            );
        default:
            return focus satisfies never;
    }
}

function getApplicableCommand(
    actionId: InputActionId,
    event: InputEvent,
    context: ReturnType<typeof resolveInputContext>,
    capture?: boolean,
    onlyCameraCenter = false,
    isInputUI = false,
    isInputBindingPanelOpen = false
): (typeof inputCommands)[InputActionId] | undefined {
    const definition = inputActions[actionId];
    const command = inputCommands[actionId];
    return command !== undefined &&
        (actionId === InputActionId.GeneralCancel ||
            !(event instanceof KeyboardEvent) ||
            (!isTextInputElement(document.activeElement) && !isTextInputElement(event.target))) &&
        (!onlyCameraCenter || actionId === InputActionId.CameraCenter) &&
        (capture === undefined || Boolean(definition.capture) === capture) &&
        (!isInputUI || definition.allowInInputUI === true) &&
        isContextActive(definition.contexts, context) &&
        isFocusAllowed(definition.focus, event, isInputBindingPanelOpen)
        ? command
        : undefined;
}

/** Runs the first applicable command bound to `chord`; `capture` restricts keyboard dispatch to one event phase. */
function dispatch(
    event: InputEvent,
    chord: InputChord,
    capture?: boolean,
    onlyCameraCenter = false,
    isInputUI = false
): boolean {
    const state = usePlacementStore.getState();
    if (state.inputCaptureTarget) {
        return false;
    }
    const context = resolveInputContext(state);
    return (state.inputBindings.index.get(chord) ?? []).some((actionId) => {
        const command = getApplicableCommand(
            actionId,
            event,
            context,
            capture,
            onlyCameraCenter,
            isInputUI,
            state.isInputBindingPanelOpen
        );
        return command !== undefined && command(event, state) !== false;
    });
}

const hasApplicableCommand = (
    event: InputEvent,
    state: PlacementState,
    chord: InputChord,
    onlyCameraCenter = false,
    capture?: boolean,
    isInputUI = false,
    allowedActionIds?: readonly InputActionId[]
) => {
    if (state.inputCaptureTarget) {
        return false;
    }
    const context = resolveInputContext(state);
    return (state.inputBindings.index.get(chord) ?? []).some(
        (actionId) =>
            (!allowedActionIds || allowedActionIds.includes(actionId)) &&
            getApplicableCommand(
                actionId,
                event,
                context,
                capture,
                onlyCameraCenter,
                isInputUI,
                state.isInputBindingPanelOpen
            ) !== undefined
    );
};

const panelQuickReplacementDirectionActions = [
    InputActionId.ArticleQuickReplacementLeft,
    InputActionId.ArticleQuickReplacementRight,
    InputActionId.ArticleQuickReplacementUp,
    InputActionId.ArticleQuickReplacementDown
] as const;

const handleKeyDown = (capture: boolean) => (event: KeyboardEvent) => {
    const state = usePlacementStore.getState();
    const isInputUI = event.target instanceof Element && event.target.closest('[data-input-ui]') !== null;
    const chord = chordFromKeyboardEvent(event);
    const isPanelDirectionModifier =
        chord !== null &&
        state.isPanelQuickReplacementOpen &&
        isPanelQuickReplacementInwardModifierActive(state, event);
    const baseChord = isPanelDirectionModifier ? formatChord({ modifiers: [], code: event.code }) : null;
    const hasChordBinding = chord !== null && hasApplicableCommand(event, state, chord, false, undefined, isInputUI);
    const hasBaseBinding =
        baseChord !== null &&
        hasApplicableCommand(
            event,
            state,
            baseChord,
            false,
            undefined,
            isInputUI,
            panelQuickReplacementDirectionActions
        );
    const didDispatchChord = chord ? dispatch(event, chord, capture, false, isInputUI) : false;
    const didDispatchBase =
        !capture && !didDispatchChord && baseChord && hasBaseBinding
            ? dispatch(event, baseChord, capture, false, isInputUI)
            : false;

    if (
        didDispatchChord ||
        didDispatchBase ||
        ((capture === undefined || capture) && (hasChordBinding || hasBaseBinding))
    ) {
        event.preventDefault();
    }
};
const handleCaptureKeyDown = handleKeyDown(true);
const handleBubbleKeyDown = handleKeyDown(false);

let pendingClick:
    | {
          event: PointerEvent;
          chord: InputChord;
          onlyCameraCenter: boolean;
          target: EventTarget | null;
          button: number;
          pointerId: number;
          timeStamp: number;
          timer: ReturnType<typeof setTimeout>;
      }
    | undefined;

const isCanvasEvent = (event: Event) => event.target instanceof HTMLCanvasElement;

let focusPress: { event: PointerEvent; moved: boolean } | undefined;
let completedFocusClick: PointerEvent | undefined;

const isNearby = (first: PointerEvent, second: PointerEvent) =>
    Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) <= DRAG_THRESHOLD_PX;

/** Focus gestures wait for release so either press can become camera navigation instead. */
export function handlePointerDown(event: PointerEvent): void {
    updateInputPointerPosition(event);
    if (focusPress) {
        focusPress = undefined;
        completedFocusClick = undefined;
    }
    if (!isCanvasEvent(event)) {
        completedFocusClick = undefined;
        return;
    }
    const state = usePlacementStore.getState();
    if (
        hasApplicableCommand(
            event,
            state,
            chordFromMouseEvent(event, InputTrigger.DoubleClick),
            event.target !== state.mainWebGLRendererRef.current?.domElement,
            undefined,
            false,
            [InputActionId.CameraCenter]
        )
    ) {
        focusPress = {
            event,
            moved: event.buttons !== (event.button === 0 ? 1 : event.button === 1 ? 4 : 2)
        };
        return;
    }
    completedFocusClick = undefined;
    handleClick(event, getClickCount(event));
}

function handleFocusPointerUp(event: PointerEvent): void {
    updateInputPointerPosition(event);
    const press = focusPress;
    if (!press || press.event.pointerId !== event.pointerId || press.event.button !== event.button) {
        return;
    }
    focusPress = undefined;
    const down = press.event;
    if (
        press.moved ||
        !isNearby(down, event) ||
        event.target !== down.target ||
        event.buttons !== 0 ||
        event.timeStamp - down.timeStamp > DOUBLE_CLICK_MS ||
        chordFromMouseEvent(event, InputTrigger.Click) !== chordFromMouseEvent(down, InputTrigger.Click)
    ) {
        completedFocusClick = undefined;
        return;
    }
    const previous = completedFocusClick;
    const elapsed = previous ? down.timeStamp - previous.timeStamp : Infinity;
    const isDoubleClick =
        previous !== undefined &&
        previous.target === down.target &&
        previous.pointerId === down.pointerId &&
        previous.button === down.button &&
        chordFromMouseEvent(previous, InputTrigger.Click) === chordFromMouseEvent(down, InputTrigger.Click) &&
        isNearby(previous, down) &&
        elapsed >= 0 &&
        elapsed <= DOUBLE_CLICK_MS;
    completedFocusClick = isDoubleClick ? undefined : down;
    handleClick(down, isDoubleClick ? 2 : 1);
}

function resetPointerClicks(): void {
    focusPress = undefined;
    completedFocusClick = undefined;
    clearTimeout(pendingClick?.timer);
    pendingClick = undefined;
}

/** Single clicks wait only when the same gesture also has a double-click command. */
function handleClick(event: PointerEvent, clickCount: 1 | 2): void {
    const state = usePlacementStore.getState();
    const isMainCanvas = event.target === state.mainWebGLRendererRef.current?.domElement;
    const clickChord = chordFromMouseEvent(event, InputTrigger.Click);
    const doubleClickChord = chordFromMouseEvent(event, InputTrigger.DoubleClick);
    const onlyCameraCenter = !isMainCanvas;
    const previousClick = pendingClick;
    const isSameSequence =
        clickCount === 2 &&
        previousClick?.target === event.target &&
        previousClick.button === event.button &&
        previousClick.pointerId === event.pointerId &&
        previousClick.chord === clickChord &&
        previousClick.onlyCameraCenter === onlyCameraCenter &&
        event.timeStamp - previousClick.timeStamp >= 0 &&
        event.timeStamp - previousClick.timeStamp <= DOUBLE_CLICK_MS;

    if (previousClick) {
        clearTimeout(previousClick.timer);
        pendingClick = undefined;
    }

    if (isSameSequence && previousClick) {
        if (
            hasApplicableCommand(event, state, doubleClickChord, onlyCameraCenter) &&
            dispatch(event, doubleClickChord, undefined, onlyCameraCenter)
        ) {
            return;
        }
        dispatch(previousClick.event, previousClick.chord, undefined, previousClick.onlyCameraCenter);
        return;
    }

    if (previousClick) {
        dispatch(previousClick.event, previousClick.chord, undefined, previousClick.onlyCameraCenter);
    }

    if (clickCount === 2) {
        if (
            hasApplicableCommand(event, state, doubleClickChord, onlyCameraCenter) &&
            dispatch(event, doubleClickChord, undefined, onlyCameraCenter)
        ) {
            return;
        }
        dispatch(event, clickChord, undefined, onlyCameraCenter);
        return;
    }

    if (
        hasApplicableCommand(event, state, doubleClickChord, onlyCameraCenter) &&
        hasApplicableCommand(event, state, clickChord, onlyCameraCenter)
    ) {
        const nextClick = {
            event,
            chord: clickChord,
            onlyCameraCenter,
            target: event.target,
            button: event.button,
            pointerId: event.pointerId,
            timeStamp: event.timeStamp,
            timer: undefined as unknown as ReturnType<typeof setTimeout>
        };
        nextClick.timer = setTimeout(() => {
            if (pendingClick !== nextClick) {
                return;
            }
            pendingClick = undefined;
            dispatch(nextClick.event, nextClick.chord, undefined, nextClick.onlyCameraCenter);
        }, DOUBLE_CLICK_MS);
        pendingClick = nextClick;
        return;
    }
    dispatch(event, clickChord, undefined, onlyCameraCenter);
}

function handleContextMenu(event: MouseEvent): void {
    if (isCanvasEvent(event)) {
        event.preventDefault();
    }
}

function handlePointerMove(event: PointerEvent): void {
    if (focusPress?.event.pointerId === event.pointerId && !isNearby(focusPress.event, event)) {
        focusPress.moved = true;
    }
    updateInputPointerPosition(event);
}

/** Installs the global input listeners that route events to commands via the active keymap. */
export const useInputEventHandler = () => {
    useEffect(() => {
        window.addEventListener('keydown', handleCaptureKeyDown, true);
        window.addEventListener('keydown', handleBubbleKeyDown);
        window.addEventListener('pointerdown', handlePointerDown, true);
        window.addEventListener('pointerup', handleFocusPointerUp, true);
        window.addEventListener('pointercancel', resetPointerClicks, true);
        window.addEventListener('blur', resetPointerClicks);
        window.addEventListener('contextmenu', handleContextMenu, true);
        window.addEventListener('pointermove', handlePointerMove, { passive: true, capture: true });
        return () => {
            resetPointerClicks();
            window.removeEventListener('keydown', handleCaptureKeyDown, true);
            window.removeEventListener('keydown', handleBubbleKeyDown);
            window.removeEventListener('pointerdown', handlePointerDown, true);
            window.removeEventListener('pointerup', handleFocusPointerUp, true);
            window.removeEventListener('pointercancel', resetPointerClicks, true);
            window.removeEventListener('blur', resetPointerClicks);
            window.removeEventListener('contextmenu', handleContextMenu, true);
            window.removeEventListener('pointermove', handlePointerMove, true);
        };
    }, []);
};
